import { FC } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AppMenuKey, AppMenuSectionItem } from "../../../../../common/routesUtils";
import MultiValueWidget from "../../../../../components/charts/MultiValueWidget";
import { View } from "../../../../../components/layout/View";
import { PageType } from "../../../../../config/page.types";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import { EntityTripDetailsDTO } from "../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../hooks/useResource";
import TripEmissionDetails from "../TransportGreenrouterDetail/components/TripEmissionDetails";
import TripMap from "../TransportGreenrouterDetail/components/TripMap";
import { displayValues } from "./data";

interface Props {
	menuKeys?: Array<AppMenuKey>;
	path?: Array<AppMenuSectionItem>;
}

const TransportGreenrouterDetail: FC<Props> = ({ menuKeys, path }) => {
	const { t, i18n } = useTranslation("feature.results");

	const { appliedFilters, registry } = useFiltersRegistry();

	const params = useParams();
	const entityId = params.entityId || ""; // NOTE: redirect in case of id not given
	const transportId = params.transportId || "1"; // NOTE: redirect in case of id not given

	const { data, isLoading, reload } = useReadResource<undefined, EntityTripDetailsDTO>({
		route: ApiRoutes.ResultsEntityDetails,
		path: { entityId, transportId },
	});

	const { entityData, transportData, transportTotalEmissions, appliedFiltersToDetail } = displayValues(
		data,
		t,
		i18n.language,
		appliedFilters.payload,
		registry
	);

	const headerContext = {
		refresh: reload,
		loading: isLoading,
	};

	return (
		<View pageType={PageType.ResultsTransportGreenRouterEntityDetail} headerContext={headerContext}>
			<Grid container spacing={2}>
				<Grid size={12} minHeight={128}>
					<MultiValueWidget
						isLoading={isLoading}
						values={transportTotalEmissions}
						valueVariant="h5"
						title={t("tripDetails/entity total emissions")}
						elementsPerRow={6}
					/>
				</Grid>
				<Grid size={12} minHeight={150}>
					<MultiValueWidget
						isLoading={isLoading}
						values={entityData}
						valueVariant="body1"
						title={t("tripDetails/entity data")}
						elementsPerRow={5}
					/>
				</Grid>
				<Grid size={12} minHeight={150}>
					<MultiValueWidget
						isLoading={isLoading}
						values={transportData}
						valueVariant="body1"
						title={t("tripDetails/transport data")}
						elementsPerRow={5}
					/>
				</Grid>
				{appliedFiltersToDetail.length > 0 && (
					<Grid size={12} minHeight={150}>
						<MultiValueWidget
							isLoading={isLoading}
							values={appliedFiltersToDetail}
							valueVariant="body1"
							title={t("applied filters", { ns: "common" })}
							elementsPerRow={5}
							areFilters={true}
						/>
					</Grid>
				)}

				<Grid size={12} minHeight={500}>
					<TripMap arcs={data?.arcs} isLoading={isLoading} />
				</Grid>
				<Grid size={12} minHeight={500}>
					<TripEmissionDetails arcs={data?.arcs} isLoading={isLoading} isEntity />
				</Grid>
			</Grid>
		</View>
	);
};

export default TransportGreenrouterDetail;
